/**
 * TaskStatusActions — status-transition button group (Sprint Calendar T2.4.3).
 *
 * Renders the set of allowed status-changing buttons for a task. The
 * underlying state machine matches the backend's ``ALLOWED_TRANSITIONS``
 * in ``app/services/task_service.py`` so the UI never offers an action
 * the server will reject with 409.
 *
 *   todo        → start, block, complete
 *   in_progress → pause, block, complete
 *   blocked     → start, unblock, complete
 *   done        → reopen
 *   cancelled   → (empty — terminal)
 *
 * Note on the dual idempotency model: ``start`` / ``pause`` / ``block`` /
 * ``unblock`` are STRICT on the backend (409 on invalid current status,
 * zero audit rows on rejection). ``complete`` / ``reopen`` remain
 * idempotent (200 with current row, no extra audit). This component
 * does not need to care — it only ever fires an action that is locally
 * marked allowed for the displayed status. A 409 surfacing here means
 * the local view is stale; the parent ``onChanged`` callback refetches
 * to recover.
 *
 * Props:
 *   - task: { id, status }
 *   - variant: "detail" | "list" — wide vs compact button styling
 *   - onChanged: optional callback invoked after a successful transition
 *     (with the updated task from the response). Parent typically
 *     refetches its own list / detail to pick up enrichment fields the
 *     wire response doesn't include.
 *   - disabled: optional — disable all buttons (parent-controlled busy).
 *
 * Server-authoritative contract: never sends ``current_status`` to the
 * backend. The button row reflects local state for UX only — the
 * server makes the final transition decision from the row it loads.
 *
 * Empty-state (cancelled): the component returns ``null`` so callers
 * can rely on layout collapsing rather than special-case-checking the
 * status themselves.
 */
import React, { useState } from "react";
import { toast } from "sonner";

import {
  blockAdminTask,
  completeAdminTask,
  pauseAdminTask,
  reopenAdminTask,
  startAdminTask,
  unblockAdminTask,
} from "../../../api/adminData";


// ──────────────────────────────────────────────────────────────────────
// State machine — mirrors backend ALLOWED_TRANSITIONS exactly. Keep
// this in sync if the backend table ever changes.
// ──────────────────────────────────────────────────────────────────────

const ALLOWED_TRANSITIONS = {
  todo: ["start", "block", "complete"],
  in_progress: ["pause", "block", "complete"],
  blocked: ["start", "unblock", "complete"],
  done: ["reopen"],
  cancelled: [],
};

// Action → (label, success-toast-text, API-call). The function returns
// a Promise of the updated TaskRead which the caller may forward to
// onChanged.
const ACTION_SPECS = {
  start: {
    label: "Starten",
    successToast: "Aufgabe gestartet.",
    call: (taskId) => startAdminTask(taskId),
  },
  pause: {
    label: "Pausieren",
    successToast: "Aufgabe pausiert.",
    call: (taskId) => pauseAdminTask(taskId),
  },
  block: {
    label: "Blockieren",
    successToast: "Aufgabe blockiert.",
    call: (taskId) => blockAdminTask(taskId),
  },
  unblock: {
    label: "Entsperren",
    successToast: "Blockierung aufgehoben.",
    call: (taskId) => unblockAdminTask(taskId),
  },
  complete: {
    label: "Erledigt",
    successToast: "Aufgabe als erledigt markiert.",
    call: (taskId) => completeAdminTask(taskId),
  },
  reopen: {
    label: "Wieder öffnen",
    successToast: "Aufgabe wieder geöffnet.",
    // Default ``new_status`` ("todo") matches the workflow expectation:
    // re-opening a done task should land it back in the active-work
    // pool, not in another terminal/cancelled state.
    call: (taskId) => reopenAdminTask(taskId, "todo"),
  },
};


// ──────────────────────────────────────────────────────────────────────
// Styling presets — picked so positive moves are blue-toned (start /
// unblock / reopen), destructive moves are red-toned (block), and the
// "done" action keeps its green accent that the previous row-actions
// row used.
//
// S6 theme fix: LIGHT mode gets pastel palette tones (the previous
// hex-only tones rendered as near-black chips on the light page);
// DARK mode keeps the previous appearance byte-for-byte via dark:
// pins, including the border-only hover. The light borders use the
// -300 shades on purpose — the light-mode CSS restyles some
// border-*-200/bg-*-50 combinations globally, and these buttons must
// stay outlined, not become solid badges.
// ──────────────────────────────────────────────────────────────────────

const TONE_BY_ACTION = {
  start:
    "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 " +
    "dark:border-[#1e2238] dark:bg-[#0d0f14] dark:text-[#8fb3ff] dark:hover:bg-[#0d0f14] dark:hover:border-[#3a4575]",
  pause:
    "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 " +
    "dark:border-[#3a2f1f] dark:bg-[#1e160d] dark:text-[#f0b86e] dark:hover:bg-[#1e160d] dark:hover:border-[#7a5a2d]",
  block:
    "border-red-300 bg-red-50 text-red-700 hover:bg-red-100 " +
    "dark:border-[#3a1f24] dark:bg-[#1e0d10] dark:text-[#ff6b6b] dark:hover:bg-[#1e0d10] dark:hover:border-[#ff6b6b]",
  unblock:
    "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 " +
    "dark:border-[#1e2238] dark:bg-[#0d0f14] dark:text-[#8fb3ff] dark:hover:bg-[#0d0f14] dark:hover:border-[#3a4575]",
  complete:
    "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 " +
    "dark:border-[#1f3a2a] dark:bg-[#0d1e15] dark:text-[#6ee7a8] dark:hover:bg-[#0d1e15] dark:hover:border-[#2a5a40]",
  reopen:
    "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 " +
    "dark:border-[#1e2238] dark:bg-[#0d0f14] dark:text-[#8fb3ff] dark:hover:bg-[#0d0f14] dark:hover:border-[#3a4575]",
};


// ──────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────

export default function TaskStatusActions({
  task,
  variant = "list",
  onChanged,
  disabled = false,
}) {
  // Local busy guard prevents double-fire if the parent doesn't pass
  // a ``disabled`` prop. We still respect the parent's flag too.
  const [busy, setBusy] = useState(false);

  if (!task || !task.status) return null;

  const actions = ALLOWED_TRANSITIONS[task.status] || [];
  if (actions.length === 0) return null;

  async function handle(action) {
    const spec = ACTION_SPECS[action];
    if (!spec) return; // should never happen — defensive
    setBusy(true);
    try {
      const updated = await spec.call(task.id);
      toast.success(spec.successToast);
      if (typeof onChanged === "function") {
        // Pass through the wire response; callers may use it directly or
        // ignore it and refetch from the source (preferred when the
        // list endpoint enriches fields that the detail endpoint omits).
        onChanged(updated);
      }
    } catch (err) {
      // The backend builds a localized German detail for 409s
      // (Aktion 'X' ist im aktuellen Status 'Y' nicht erlaubt.) which is
      // safe to surface verbatim. Other status codes fall back to the
      // generic "Aktion fehlgeschlagen." string so we never leak raw
      // backend internals on 5xx.
      const fallback = "Aktion fehlgeschlagen.";
      toast.error(err?.message || fallback);
    } finally {
      setBusy(false);
    }
  }

  const isCompact = variant === "list";
  // S6 a11y: an explicit focus-visible ring so keyboard focus is
  // clearly visible in light AND dark (both themes define --ring).
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const buttonBase = isCompact
    ? `rounded-[8px] border px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 ${focusRing}`
    : `rounded-[8px] border px-4 py-2 text-[13px] font-semibold disabled:opacity-50 min-h-[40px] ${focusRing}`;

  const groupClass = isCompact
    ? "flex flex-wrap items-center gap-2"
    : "flex flex-wrap items-center gap-2";

  return (
    <div className={groupClass}>
      {actions.map((action) => {
        const spec = ACTION_SPECS[action];
        return (
          <button
            key={action}
            type="button"
            onClick={() => handle(action)}
            disabled={disabled || busy}
            className={`${buttonBase} ${TONE_BY_ACTION[action]}`}
            data-action={action}
          >
            {spec.label}
          </button>
        );
      })}
    </div>
  );
}
